/**
 * RouteSkeleton — Reusable loading skeletons for route content areas.
 *
 * Used by Phase 4 loaders: routes render these immediately (SSR) while
 * the deferred data streams in.
 */

/**
 * Generic list skeleton (e.g. todos, tasks, notifications).
 */
export function ListSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 rounded-lg border bg-card p-3 animate-pulse"
        >
          <div className="h-5 w-5 rounded-full bg-muted" />
          <div className="flex-1 space-y-1.5">
            <div className="h-4 w-3/4 rounded bg-muted" />
            <div className="h-3 w-1/3 rounded bg-muted" />
          </div>
          <div className="h-5 w-16 rounded-full bg-muted" />
        </div>
      ))}
    </div>
  );
}

/**
 * Card grid skeleton (e.g. clients, projects).
 */
export function CardGridSkeleton({ cards = 6 }: { cards?: number }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {Array.from({ length: cards }).map((_, i) => (
        <div
          key={i}
          className="rounded-xl border bg-card p-5 space-y-3 animate-pulse"
        >
          <div className="flex items-center justify-between">
            <div className="h-5 w-1/2 rounded bg-muted" />
            <div className="h-5 w-12 rounded-full bg-muted" />
          </div>
          <div className="h-3 w-full rounded bg-muted" />
          <div className="h-3 w-2/3 rounded bg-muted" />
        </div>
      ))}
    </div>
  );
}

/**
 * Stats row skeleton (e.g. dashboard overview).
 */
export function StatsRowSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="h-28 rounded-xl border bg-muted/30 animate-pulse"
        />
      ))}
    </div>
  );
}

/**
 * Page-level skeleton that combines a header area + list.
 */
export function PageSkeleton({
  rows = 6,
  variant = "list",
}: {
  rows?: number;
  variant?: "list" | "cards";
}) {
  return (
    <div className="container mx-auto p-6 space-y-6 max-w-3xl">
      {/* Header skeleton */}
      <div className="flex items-center justify-between animate-pulse">
        <div className="h-8 w-48 rounded bg-muted" />
        <div className="h-9 w-24 rounded-md bg-muted" />
      </div>
      {/* Filter bar skeleton */}
      <div className="flex items-center gap-2 animate-pulse">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-7 w-16 rounded-full bg-muted" />
        ))}
      </div>
      {/* Content skeleton */}
      {variant === "list" ? (
        <ListSkeleton rows={rows} />
      ) : (
        <CardGridSkeleton cards={rows} />
      )}
    </div>
  );
}
